/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { Dispatch, SetStateAction } from 'react';
import * as React from 'react';

import { copyTest } from '@coursera-web/copy-test';

import { Grid, LogoWrapper, breakpoints, useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import type { ExpressionOfInterestTextProps } from 'bundles/expression-of-interest/types';
import type { Maybe } from 'bundles/premium-certificate/__generated__/baseContentfulTypes';
import type {
  ChecklistEnrollmentFragment,
  ExpressionOfInterestContainerFragment,
  MastertrackPartnerInformationFragment,
} from 'bundles/premium-certificate/__generated__/contentfulTypes';
import CertificateBreadcrumbs from 'bundles/premium-certificate/components/mastertrack/CertificateBreadcrumbs';
import CertificateEnrollmentFlow from 'bundles/premium-certificate/components/mastertrack/enrollment/CertificateEnrollmentFlow';
import Container from 'bundles/xddp/components/shared/Container';

import _t from 'i18n!nls/premium-certificate';

type Props = {
  slug?: Maybe<string>;
  mastertrackId?: string;
  certificateName?: Maybe<string>;
  certificateType?: Maybe<string>;
  partnerInformation?: Maybe<MastertrackPartnerInformationFragment>;
  expressionOfInterestContainer?: Maybe<ExpressionOfInterestContainerFragment>;
  bannerDescription?: Maybe<string>;
  isScrollPastBanner: boolean;
  setEmbeddedRegFormDisplayed: Dispatch<SetStateAction<boolean>>;
} & ChecklistEnrollmentFragment;

type BannerContentProps = {
  certificateName?: Maybe<string>;
  certificateType?: Maybe<string>;
  bannerDescription?: Maybe<string>;
  partnerName?: Maybe<string>;
  partnerMonotoneLogo?: Maybe<{
    url?: Maybe<string>;
  }>;
};

const styles = {
  bannerContentCol: () => css`
    ${breakpoints.down('md')} {
      margin-bottom: 5rem;
    }
  `,
  enrollmentContentCol: () => css`
    ${breakpoints.up('md')} {
      margin-left: 8.33333%; /* stylelint-disable-line number-max-precision */
      position: initial !important;
    }
    ${breakpoints.up('lg')} {
      margin-left: 16.6667%;
    }
  `,
  certificateInformationContainer: css`
    display: flex;
    align-items: center;
  `,
  partnerLogoContainer: () => css`
    display: inline-block;
    height: 55px;
    ${breakpoints.down('md')} {
      height: 40px;
    }
  `,
  partnerLogo: css`
    max-height: 100%;
    max-width: 100%;
  `,
  certificateType: () => css`
    color: var(--cds-color-grey-600);
    font-size: 1.25rem;
    line-height: 1.5rem;
    margin-inline-start: 1.5rem;
    ${breakpoints.down('md')} {
      font-size: 1rem;
      line-height: 1.875rem;
    }
  `,
  certificateName: () => css`
    color: var(--cds-color-neutral-primary);
    font-family: inherit;
    line-height: 2.625rem;
    font-weight: 700;
    font-size: 2.125rem;
    margin: 1rem 0;
  `,
  bannerDescription: () => css`
    color: var(--cds-color-neutral-primary);
    font-size: 1rem;
    line-height: 1.875rem;
  `,
  backgroundContainer: () => css`
    position: absolute;
    top: 0;
    right: 0;
    width: 100%;
    height: 100%;
    ${breakpoints.up('md')} {
      overflow: hidden;
      width: 50%;
    }
  `,
  certificateBannerWrapper: (_: Theme, isScrollPastBanner: boolean) => css`
    position: relative;
    background-color: var(--cds-color-neutral-background-primary-weak);
    margin-bottom: ${isScrollPastBanner && '66px'};

    ${breakpoints.down('md')} {
      overflow: hidden;
    }

    .bannerContentContainer {
      padding: 2rem 1.5rem 5rem 1.5rem;
      ${breakpoints.down('md')} {
        padding-bottom: 2rem;
      }
    }
  `,
  backgroundGradient: (
    _: Theme,
    primaryColor: Maybe<string> | undefined,
    secondaryColor: Maybe<string> | undefined
  ) => css`
    position: relative;
    top: -50%;
    left: 0;
    min-width: 1000px;
    height: 200%;
    border-top-left-radius: 50%;
    border-bottom-left-radius: 50%;
    background: ${primaryColor && primaryColor};
    border: ${secondaryColor && `24px solid ${secondaryColor}`};

    ${breakpoints.down('sm')} {
      left: -950px;
      top: 45%;
      width: 2500px;
      height: 2000px;
      border-top-right-radius: 50%;
      border-bottom-left-radius: initial;
    }
  `,
  backgroundOverlay: (_: Theme, primaryColor: Maybe<string> | undefined) => css`
    position: absolute;
    right: 0;
    width: 30%;
    height: 100%;
    top: 0;
    background: ${primaryColor && primaryColor};

    ${breakpoints.down('sm')} {
      display: none;
    }
  `,
};

export const BannerContent = ({
  certificateName,
  certificateType,
  bannerDescription,
  partnerName,
  partnerMonotoneLogo,
}: BannerContentProps) => (
  <div>
    <div css={styles.certificateInformationContainer}>
      {partnerMonotoneLogo?.url && (
        <LogoWrapper paddingVariant="smallOutside" css={styles.partnerLogoContainer} data-test="partnerLogoContainer">
          <img css={styles.partnerLogo} src={partnerMonotoneLogo.url} alt={partnerName || _t('Partner Logo')} />
        </LogoWrapper>
      )}
      <span css={styles.certificateType}>{certificateType}</span>
    </div>
    <h1 data-e2e="page-title" css={styles.certificateName}>
      {certificateName}
    </h1>
    <p data-e2e="page-tagline" css={styles.bannerDescription}>
      {bannerDescription}
    </p>
  </div>
);

const CertificateBanner = ({
  certificateName,
  certificateType,
  partnerInformation,
  bannerDescription,
  expressionOfInterestContainer,
  slug,
  isScrollPastBanner,
  setEmbeddedRegFormDisplayed,
  mastertrackId,
  enrollmentChecklistHeading,
  enrollmentChecklistItemsCollection,
}: Props) => {
  const theme = useTheme();
  if (!partnerInformation || !certificateName) {
    return null;
  }

  const { partnerName, partnerMonotoneLogo, primaryColor, secondaryColor } = partnerInformation;

  const createEOICopyTest = () => {
    if (expressionOfInterestContainer) {
      const { applyButtonText, emailMeButtonText, emailMeFormHeaderText, programInterestText } =
        expressionOfInterestContainer;
      return {
        ...expressionOfInterestContainer,
        applyButtonText: copyTest(`contentful-mtdp-eoi-applyButtonText-${slug}-en`, applyButtonText),
        emailMeButtonText: copyTest(`contentful-mtdp-eoi-emailMeButtonText-${slug}-en`, emailMeButtonText),
        emailMeFormHeaderText: copyTest(`contentful-mtdp-eoi-emailMeFormHeaderText-${slug}-en`, emailMeFormHeaderText),
        programInterestText: copyTest(`contentful-mtdp-eoi-programInterestText-${slug}-en`, programInterestText),
      };
    }
    return expressionOfInterestContainer;
  };

  return (
    <div
      id="CertificateBanner"
      data-test="CertificateBanner"
      css={styles.certificateBannerWrapper(theme, isScrollPastBanner)}
    >
      <div css={styles.backgroundContainer}>
        <div css={styles.backgroundGradient(theme, primaryColor, secondaryColor)} />
        <div css={styles.backgroundOverlay(theme, primaryColor)} />
      </div>
      <Container className="bannerContentContainer">
        <Grid container className="hidden-sm-down">
          <Grid item md={6}>
            <CertificateBreadcrumbs certificateName={certificateName} certificateType={certificateType} />
          </Grid>
        </Grid>
        <Grid container spacing={24}>
          <Grid item md={6} css={styles.bannerContentCol}>
            <BannerContent
              certificateName={certificateName}
              certificateType={certificateType}
              bannerDescription={bannerDescription}
              partnerName={partnerName}
              partnerMonotoneLogo={partnerMonotoneLogo}
            />
          </Grid>
          <Grid item css={styles.enrollmentContentCol} lg={4} md={5}>
            <CertificateEnrollmentFlow
              slug={slug}
              mastertrackId={mastertrackId}
              certificateName={certificateName}
              partnerName={partnerName}
              partnerMonotoneLogo={partnerMonotoneLogo?.url}
              isScrollPastBanner={isScrollPastBanner}
              setEmbeddedRegFormDisplayed={setEmbeddedRegFormDisplayed}
              expressionOfInterestContainer={createEOICopyTest() as ExpressionOfInterestTextProps}
              enrollmentChecklistHeading={enrollmentChecklistHeading}
              enrollmentChecklistItemsCollection={enrollmentChecklistItemsCollection}
              certificateType={certificateType}
            />
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

export default CertificateBanner;
